import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Download } from "lucide-react";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, CtaBand } from "@/components/MarketingShell";

const TITLE = "Free Security D.A.R. & Incident Report Templates (Word, PDF)";
const DESC =
  "Download a free security guard Daily Activity Report (D.A.R.) template and incident report template in Word and PDF. Filled-in examples included. No email required.";

export const Route = createFileRoute("/templates")({
  component: TemplatesPage,
  head: () => ({
    meta: [
      { title: `${TITLE} — MissionReady` },
      { name: "description", content: DESC },
      ...socialMeta({ title: TITLE, description: DESC, url: "https://missionready.dev/templates" }),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/templates" }],
    scripts: [...ORG_WEBSITE_JSONLD],
  }),
});

const DAR_EXAMPLE = [
  ["18:00", "On post. Received pass-down from day officer; no open issues."],
  ["18:30", "Exterior patrol complete. All dock doors secured."],
  ["20:15", "Vendor truck (ACME Freight) admitted after manifest check."],
  ["22:40", "Found Gate 5 padlock unlatched. Secured; photo taken; supervisor notified."],
  ["01:10", "Interior patrol complete. No issues."],
  ["06:00", "Pass-down to relief officer. Off post."],
];

function DownloadButtons({ base }: { base: string }) {
  return (
    <div className="mr-hero-actions">
      <a href={`/downloads/${base}.docx`} download className="mr-button">
        <Download /> WORD (.DOCX)
      </a>
      <a href={`/downloads/${base}.pdf`} download className="mr-button-secondary">
        <Download /> PDF
      </a>
    </div>
  );
}

function TemplatesPage() {
  return (
    <MarketingShell>
      <section className="mr-product-hero">
        <div className="mr-grid-bg" aria-hidden="true" />
        <div className="mr-shell">
          <span className="mr-live-chip"><i /> Free download · No email required</span>
          <h1>Free D.A.R. &amp; incident report templates</h1>
          <p>
            Print-ready security guard templates in Word and PDF. Each file has a blank form plus a
            filled-in example, so new officers know exactly what a good report looks like.
          </p>
        </div>
      </section>

      <section className="mr-capabilities">
        <div className="mr-shell">
          <div className="mr-section-heading"><h2>Daily Activity Report (D.A.R.) template</h2></div>
          <p className="mr-section-intro">
            Header fields for officer, post, client and shift times, a timed activity log, and an
            officer signature line. Page 2 is a completed example from a night shift.
          </p>
          <DownloadButtons base="dar-template" />
          <h3 style={{ marginTop: "2rem" }}>Example D.A.R. entries</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "0.75rem" }}>
              <thead>
                <tr><th style={{ textAlign: "left", padding: 8 }}>Time</th><th style={{ textAlign: "left", padding: 8 }}>Activity / observation</th></tr>
              </thead>
              <tbody>
                {DAR_EXAMPLE.map(([t, a]) => (
                  <tr key={t} style={{ borderTop: "1px solid currentColor", opacity: 0.9 }}>
                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>{t}</td>
                    <td style={{ padding: 8 }}>{a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mr-capabilities">
        <div className="mr-shell">
          <div className="mr-section-heading"><h2>Security incident report template</h2></div>
          <p className="mr-section-intro">
            Report number, date, time, location, incident type, police and injury details, then
            sections for persons involved, narrative, actions taken, evidence and follow-up.
          </p>
          <DownloadButtons base="incident-report-template" />
          <h3 style={{ marginTop: "2rem" }}>Example narrative</h3>
          <p className="mr-section-intro">
            "During exterior patrol at 22:40, I found the Gate 5 padlock unlatched and the gate
            closed but not secured. No signs of forced entry. I checked the surrounding yard and saw
            no persons or vehicles." Write facts in order: who, what, when, where, and how. Leave
            out opinions.
          </p>
        </div>
      </section>

      <section className="mr-capabilities">
        <div className="mr-shell">
          <div className="mr-product-cta">
            <h2>Skip the paperwork with Voice D.A.R.</h2>
            <p>
              Speak your log hands-free. MissionReady transcribes it into a timestamped D.A.R. and
              keeps the original audio.
            </p>
            <Link to="/voice-dar" className="mr-button">TRY VOICE D.A.R. <ArrowRight /></Link>
            <span>Also see <Link to="/incident-reporting">incident reporting software</Link>.</span>
          </div>
        </div>
      </section>

      <CtaBand title="Free Forever for solo officers" subtitle="Run D.A.R.s, incidents, patrols and GPS in one app." />
    </MarketingShell>
  );
}
